import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { SideDrawerLayout, SIDE_DRAWER_UI } from "common/Components/SideDrawer";
import { GenericForm } from "common/Components/GenericForm";
import { FieldBlock } from "styles/formStyleComponents";
import {
  isInviteEmailFormatValid,
  useInviteEmailExists,
} from "pages/PlatformUserAccess/hooks/UseInviteEmailExists";
import { PLATFORM_USER_ACCESS_UI } from "pages/PlatformUserAccess/PlatformUserAccess.constants";
import {
  CUSTOMER_INVITE_UI,
  resolveRoleHint,
} from "../Users.constants";

const buildCustomerInviteFormSections = (roleOptions, rolesHint) => [
  {
    wrapper: FieldBlock,
    fields: [
      {
        name: "email",
        label: CUSTOMER_INVITE_UI.emailLabel,
        type: "text",
        required: true,
        inputType: "email",
        placeholder: CUSTOMER_INVITE_UI.emailPlaceholder,
        hint: CUSTOMER_INVITE_UI.emailHint,
        hintId: "side-drawer-email-hint",
        inputProps: {
          id: "side-drawer-email",
          "aria-describedby": "side-drawer-email-hint",
          "data-testid": "side-drawer-email",
        },
      },
    ],
  },
  {
    wrapper: FieldBlock,
    fields: [
      {
        name: "roles",
        label: CUSTOMER_INVITE_UI.rolesLabel,
        type: "checkboxGroup",
        required: true,
        options: roleOptions,
        labelId: "side-drawer-roles-label",
        hint: rolesHint,
      },
    ],
  },
];

const normalizeRoleValues = (roles) =>
  (roles ?? [])
    .map((role) =>
      typeof role === "string" ? role : role?.value || role?.label
    )
    .filter(Boolean);

const CustomerInviteDrawer = ({
  open,
  onClose,
  roleOptions = [],
  onSubmit,
  checkInviteEmail,
  tenantName = "",
}) => {
  const [formValues, setFormValues] = useState({
    email: "",
    roles: [],
  });
  const [formErrors, setFormErrors] = useState({});
  const roleSelectOptions = useMemo(
    () =>
      (Array.isArray(roleOptions) ? roleOptions : []).map((option) => ({
        value: String(option.value ?? option.id ?? option.label ?? ""),
        label: String(option.label ?? option.value ?? ""),
        hint: resolveRoleHint(option),
      })),
    [roleOptions]
  );

  const { checkEmailOnBlur } = useInviteEmailExists({ checkInviteEmail });

  const rolesHint =
    (formValues.roles ?? []).length > 0
      ? `${formValues.roles.length} role${
          formValues.roles.length === 1 ? "" : "s"
        } selected`
      : CUSTOMER_INVITE_UI.rolesEmptyHint;

  const resetState = () => {
    setFormValues({ email: "", roles: [] });
    setFormErrors({});
  };

  const handleCancel = () => {
    resetState();
    onClose();
  };

  const handleFormChange = (name, value) => {
    setFormValues((previous) => ({ ...previous, [name]: value }));
    if (name === "email") {
      setFormErrors((previous) => {
        if (!previous.email) {
          return previous;
        }
        const next = { ...previous };
        delete next.email;
        return next;
      });
      return;
    }
    if (name === "roles") {
      setFormErrors((previous) => {
        if (!previous.roles) {
          return previous;
        }
        const next = { ...previous };
        delete next.roles;
        return next;
      });
    }
  };

  const handleEmailBlur = async (_name, emailValue) => {
    const trimmed = String(emailValue ?? "").trim();
    if (trimmed && !isInviteEmailFormatValid(trimmed)) {
      setFormErrors((previous) => ({
        ...previous,
        email: PLATFORM_USER_ACCESS_UI.invalidEmailError,
      }));
      return;
    }
    const { fieldError } = await checkEmailOnBlur(emailValue);
    if (fieldError) {
      setFormErrors((previous) => ({ ...previous, email: fieldError }));
      return;
    }
    setFormErrors((previous) => {
      if (
        !previous.email ||
        previous.email ===
          PLATFORM_USER_ACCESS_UI.inviteEmailAlreadyExistsError ||
        previous.email ===
          PLATFORM_USER_ACCESS_UI.inviteEmailValidationFailedError ||
        previous.email === PLATFORM_USER_ACCESS_UI.invalidEmailError
      ) {
        const next = { ...previous };
        delete next.email;
        return next;
      }
      return previous;
    });
  };

  const handleSubmit = async () => {
    const email = String(formValues.email ?? "").trim();
    const rolesNormalized = normalizeRoleValues(formValues.roles);

    const nextFormErrors = {};
    if (!isInviteEmailFormatValid(email)) {
      nextFormErrors.email = PLATFORM_USER_ACCESS_UI.invalidEmailError;
    }
    if (!rolesNormalized.length) {
      nextFormErrors.roles =
        PLATFORM_USER_ACCESS_UI.customerAccessRolesRequiredError;
    }
    if (
      formErrors.email ===
        PLATFORM_USER_ACCESS_UI.inviteEmailAlreadyExistsError ||
      formErrors.email ===
        PLATFORM_USER_ACCESS_UI.inviteEmailValidationFailedError
    ) {
      nextFormErrors.email = formErrors.email;
    }
    if (Object.keys(nextFormErrors).length > 0) {
      setFormErrors(nextFormErrors);
      return;
    }

    if (typeof checkInviteEmail === "function") {
      const { exists, error } = await checkInviteEmail(email);
      if (error) {
        setFormErrors({
          email: PLATFORM_USER_ACCESS_UI.inviteEmailValidationFailedError,
        });
        return;
      }
      if (exists) {
        setFormErrors({
          email: PLATFORM_USER_ACCESS_UI.inviteEmailAlreadyExistsError,
        });
        return;
      }
    }

    setFormErrors({});
    const success = await onSubmit({
      email,
      roles: rolesNormalized,
      sendInviteEmail: true,
    });
    if (success) {
      resetState();
    }
  };

  return (
    <SideDrawerLayout
      open={open}
      onClose={handleCancel}
      title={CUSTOMER_INVITE_UI.title}
      titleId="side-drawer-title"
      description={CUSTOMER_INVITE_UI.description(tenantName)}
      onSubmit={handleSubmit}
      submitLabel={CUSTOMER_INVITE_UI.submitLabel}
      cancelLabel={SIDE_DRAWER_UI.cancelLabel}
      submitTestId="side-drawer-submit"
    >
      <GenericForm
        sections={buildCustomerInviteFormSections(roleSelectOptions, rolesHint)}
        values={formValues}
        errors={formErrors}
        onChange={handleFormChange}
        onFieldBlur={(name, value) => {
          if (name === "email") {
            handleEmailBlur(name, value);
          }
        }}
        showBorder={false}
      />
    </SideDrawerLayout>
  );
};

CustomerInviteDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  roleOptions: PropTypes.arrayOf(PropTypes.object),
  onSubmit: PropTypes.func.isRequired,
  checkInviteEmail: PropTypes.func,
  tenantName: PropTypes.string,
};

CustomerInviteDrawer.defaultProps = {
  roleOptions: [],
  checkInviteEmail: undefined,
  tenantName: "",
};

export default CustomerInviteDrawer;
